import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Alert,
  CircularProgress,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Snackbar,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Search as SearchIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import productsService, { type MassConcreteDefault } from '../services/products';
import { useAuth } from '../context/AuthContext';
import { hasPermission } from '../utils/userPermissions';
import EmptyState from '../components/EmptyState';
import {
  HEADER_CELL_SX,
  ListToolbar,
  NameCell,
  StatusDot,
  RowActionsMenu,
  DetailStrip,
  SelectableTableRow,
  SelectableCard,
  useRowSelection,
  type RowAction,
} from '../components/ListPageShared';

type StatusFilter = 'all' | 'active' | 'inactive';

const COLUMN_COUNT = 5;

const MassConcreteLimits: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const canEdit = hasPermission(user, 'temp_limits.edit');
  const canCreate = hasPermission(user, 'temp_limits.create_group');

  const [items, setItems] = useState<MassConcreteDefault[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [searchFilter, setSearchFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [deleteTarget, setDeleteTarget] = useState<MassConcreteDefault | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [snackbar, setSnackbar] = useState<{ msg: string; severity: 'success' | 'error' } | null>(null);
  const selection = useRowSelection();

  const fetchItems = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await productsService.listMassConcreteDefaults();
      setItems(Array.isArray(data) ? data : []);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load temperature limit groups');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
  }, []);

  const isActive = (d: MassConcreteDefault) => d.is_active !== false;

  // Search applies first so the status counts follow the search box
  const searchedItems = useMemo(() => {
    if (!searchFilter.trim()) return items;
    const q = searchFilter.toLowerCase();
    return items.filter((d) =>
      (d.default_id?.toLowerCase().includes(q)) ||
      (d.description?.toLowerCase().includes(q)) ||
      (d.rev_no?.toLowerCase().includes(q))
    );
  }, [items, searchFilter]);

  const filteredItems = useMemo(() => {
    if (statusFilter === 'all') return searchedItems;
    return searchedItems.filter((d) => isActive(d) === (statusFilter === 'active'));
  }, [searchedItems, statusFilter]);

  // Keep the page in range when the list shrinks (e.g. after a delete)
  const lastPage = Math.max(0, Math.ceil(filteredItems.length / pageSize) - 1);
  const currentPage = Math.min(page, lastPage);
  const pagedItems = filteredItems.slice(currentPage * pageSize, currentPage * pageSize + pageSize);

  const activeCount = searchedItems.filter(isActive).length;

  useEffect(() => {
    setPage(0);
  }, [searchFilter, statusFilter]);

  // Matches the Mix Designs list: "12 Jul 2026 · username"
  const formatCreated = (row: MassConcreteDefault): string => {
    if (!row.date_create) return '—';
    const iso = row.date_create;
    const hasZone = /Z|[+-]\d{2}:?\d{2}$/.test(iso);
    const d = new Date(hasZone ? iso : `${iso}Z`);
    if (Number.isNaN(d.getTime())) return '—';
    const date = d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: '2-digit' });
    return row.create_by ? `${date} · ${row.create_by}` : date;
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await productsService.deleteMassConcreteDefault(deleteTarget.id);
      setSnackbar({ msg: 'Temperature limit group deleted', severity: 'success' });
      setDeleteTarget(null);
      fetchItems();
    } catch (e: any) {
      setSnackbar({ msg: e.response?.data?.detail || 'Delete failed', severity: 'error' });
    } finally {
      setDeleting(false);
    }
  };

  // -------------------------------------------------------------------------
  // Shared render fragments
  // -------------------------------------------------------------------------

  const actionsFor = (d: MassConcreteDefault) => {
    const view: RowAction = {
      key: 'view',
      label: t('users.viewDetails', 'View Details'),
      icon: <ViewIcon fontSize="small" />,
      onClick: () => navigate(`/mass-concrete-limits/${d.id}`),
    };
    const edit: RowAction | false = canEdit && {
      key: 'edit',
      label: t('common.edit', 'Edit'),
      icon: <EditIcon fontSize="small" />,
      onClick: () => navigate(`/mass-concrete-limits/${d.id}/edit`),
    };
    const remove: RowAction | false = canEdit && {
      key: 'delete',
      label: t('common.delete', 'Delete'),
      icon: <DeleteIcon fontSize="small" />,
      color: 'error',
      onClick: () => setDeleteTarget(d),
    };
    return { menu: [view, edit, remove], strip: [view, edit] };
  };

  const statusDot = (d: MassConcreteDefault) => (
    <StatusDot active={isActive(d)} activeLabel={t('common.active', 'Active')} inactiveLabel={t('common.inactive', 'Inactive')} />
  );

  const detailStrip = (d: MassConcreteDefault) => (
    <DetailStrip
      description={
        <>
          {d.description?.trim() || t('massConcreteLimit.noDescription', 'No description')}
          <Typography variant="caption" color="text.disabled" display="block">
            {t('massConcreteLimit.created', 'Created {{when}}', { when: formatCreated(d) })}
          </Typography>
        </>
      }
      actions={actionsFor(d).strip}
    />
  );

  const emptyState = (
    <EmptyState
      title={t('massConcreteLimit.noItems', 'No mass concrete limits found')}
      description={t('massConcreteLimit.noItemsDescription', 'Create a new mass concrete limit to get started.')}
      icon={<SearchIcon />}
      actionLabel={canCreate ? t('massConcreteLimit.addNew', 'Add New') : undefined}
      onAction={canCreate ? () => navigate('/mass-concrete-limits/new') : undefined}
    />
  );

  const paginationProps = {
    rowsPerPageOptions: [10, 20, 50, 100],
    component: 'div' as const,
    count: filteredItems.length,
    rowsPerPage: pageSize,
    page: currentPage,
    onPageChange: (_: unknown, newPage: number) => setPage(newPage),
    onRowsPerPageChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setPageSize(parseInt(e.target.value, 10));
      setPage(0);
    },
  };

  if (loading && items.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress />
      </Box>
    );
  }

  const filterSummary =
    searchFilter || statusFilter !== 'all'
      ? `Showing ${filteredItems.length} of ${items.length} (filtered)`
      : `Total: ${items.length}`;

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <ListToolbar
        search={searchFilter}
        onSearch={setSearchFilter}
        searchPlaceholder={t('massConcreteLimit.searchPlaceholder', 'Search by Default ID, description...')}
        statusOptions={[
          { key: 'all', label: t('massConcreteLimit.allGroups', 'All Limit Groups'), count: searchedItems.length },
          { key: 'active', label: t('common.active', 'Active'), count: activeCount },
          { key: 'inactive', label: t('common.inactive', 'Inactive'), count: searchedItems.length - activeCount },
        ]}
        status={statusFilter}
        onStatus={setStatusFilter}
        onRefresh={fetchItems}
        loading={loading}
        addLabel={canCreate ? t('massConcreteLimit.addNew', 'Add New') : undefined}
        onAdd={() => navigate('/mass-concrete-limits/new')}
      />

      {isMobile ? (
        /* Mobile card list */
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {filteredItems.length === 0 && !loading ? (
            <Paper sx={{ p: 3 }}>{emptyState}</Paper>
          ) : (
            pagedItems.map((d) => (
              <SelectableCard
                key={d.id}
                rowRef={selection.rowRef(d.id)}
                selected={selection.selectedId === d.id}
                onClick={() => selection.toggle(d.id)}
                header={
                  <>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <NameCell name={d.default_id || '—'} subtitle={d.description} />
                    </Box>
                    {statusDot(d)}
                    <RowActionsMenu actions={actionsFor(d).menu} />
                  </>
                }
                body={
                  d.rev_no && (
                    <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                      Rev {d.rev_no}
                    </Typography>
                  )
                }
                detail={detailStrip(d)}
              />
            ))
          )}
          <TablePagination
            {...paginationProps}
            labelRowsPerPage=""
            sx={{ '& .MuiTablePagination-selectLabel': { display: 'none' } }}
          />
        </Box>
      ) : (
        /* Desktop table: click a row to select it and show its detail strip */
        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={HEADER_CELL_SX}>{t('massConcreteLimit.group', 'Limit Group')}</TableCell>
                <TableCell sx={{ ...HEADER_CELL_SX, width: 100 }} align="center">{t('massConcreteLimit.revNo', 'Rev No')}</TableCell>
                <TableCell sx={{ ...HEADER_CELL_SX, width: 220 }}>{t('massConcreteLimit.dateCreated', 'Date Created')}</TableCell>
                <TableCell sx={{ ...HEADER_CELL_SX, width: 120 }}>{t('common.status', 'Status')}</TableCell>
                <TableCell sx={{ ...HEADER_CELL_SX, width: 48 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredItems.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={COLUMN_COUNT} align="center" sx={{ py: 4 }}>
                    {loading ? <Typography color="text.secondary">{t('common.loading')}</Typography> : emptyState}
                  </TableCell>
                </TableRow>
              ) : (
                pagedItems.map((d) => (
                  <SelectableTableRow
                    key={d.id}
                    rowRef={selection.rowRef(d.id)}
                    selected={selection.selectedId === d.id}
                    onClick={() => selection.toggle(d.id)}
                    colSpan={COLUMN_COUNT}
                    detail={detailStrip(d)}
                  >
                    <TableCell sx={{ maxWidth: 480 }}>
                      <NameCell name={d.default_id || '—'} subtitle={d.description} />
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', color: d.rev_no ? 'text.primary' : 'text.disabled' }}>
                        {d.rev_no || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary" noWrap>
                        {formatCreated(d)}
                      </Typography>
                    </TableCell>
                    <TableCell>{statusDot(d)}</TableCell>
                    <TableCell align="right" sx={{ pr: 1 }}>
                      <RowActionsMenu actions={actionsFor(d).menu} />
                    </TableCell>
                  </SelectableTableRow>
                ))
              )}
            </TableBody>
          </Table>
          {/* Footer: filter summary on the left, pagination on the right */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', pl: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {filterSummary}
            </Typography>
            <TablePagination {...paginationProps} labelRowsPerPage={t('common.rowsPerPage')} />
          </Box>
        </TableContainer>
      )}

      <Dialog
        open={deleteTarget !== null}
        onClose={() => !deleting && setDeleteTarget(null)}
        maxWidth="xs"
        fullWidth
        disableEscapeKeyDown={deleting}
      >
        <DialogTitle>Delete temperature limit group?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Delete &quot;{deleteTarget?.default_id || deleteTarget?.description || deleteTarget?.id}&quot;? This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteConfirm}
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={18} color="inherit" /> : <DeleteIcon />}
          >
            {deleting ? 'Deleting...' : t('common.delete', 'Delete')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar !== null}
        autoHideDuration={4000}
        onClose={() => setSnackbar(null)}
      >
        <Alert
          severity={snackbar?.severity ?? 'success'}
          onClose={() => setSnackbar(null)}
          variant="filled"
        >
          {snackbar?.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default MassConcreteLimits;
